import React, { useEffect, useState } from "react";
import Loader from "components/loader";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import formatDate from "helpers/format-date";
import DatePicker from "react-datepicker";
import { rescheduleOrder } from "actions/account/subscription";
import useToggle from "hooks/useToggle";
import fetchCalendar from "services/fetch-calendar";
import { useGlobalContext } from "context/global";
import { billingToDeliveryDate } from "utils/subscription-date";
import ReschedulingSurvey from "components/rescheduling-flow/survey";
import { filterAvailableDates } from "utils/subscription-date";
import PlaceOrder from "components/place-order";
import { TOGGLE_RESCHEDULE_MODAL } from "utils/action-types";
import clsx from "clsx/lite";

const RescheduleSubscription = ({
	showSurvey = false, callback, toggleSurvey, chargePrompt, setChargePrompt, buttonClassName, isEnabled = false
}) => {
	const { translation, language } = useGlobalContext();
	const { chargeOffsetDays, nextBillingDate, reschedule: data, attributes } = useSelector(state => state.account.subscription);
	const { latestSubOrder } = useSelector(state => state.account.profile);
	const [isSubmitting, toggleSubmitting] = useToggle(false);
	const [error, setError] = useState(false);
	const nextDeliveryDate = billingToDeliveryDate(nextBillingDate, chargeOffsetDays);
	const [selectedDate, setSelectedDate] = useState(new Date(nextDeliveryDate));
	const [isLoading, toggleLoading] = useToggle(true);
	const [zapietData, setZapietData] = useState(null);
	const dispatch = useDispatch();

	const deliveryLocationId = attributes?.find(attr => attr.key === "Delivery-Location-Id");

	const latestOrderBillingDate = new Date(latestSubOrder?.createdAt);
	const latestOrderDeliveryDate = latestOrderBillingDate.setDate(latestOrderBillingDate.getDate() + chargeOffsetDays);

	const isAlreadyScheduled = new Date(nextDeliveryDate).getTime() === selectedDate.getTime();

	const selectedBillingDate = new Date(selectedDate);
	selectedBillingDate.setDate(selectedBillingDate.getDate() - chargeOffsetDays);
	const isFutureDate = new Date(selectedBillingDate).getTime() > new Date(nextBillingDate).getTime();

	const maxDate = new Date();
	// Calculate the maximum date as 60 days from today
	maxDate.setDate(maxDate.getDate() + 60);

	useEffect(() => {
		if (!deliveryLocationId || zapietData) return;
		if (error) setError(false);
		fetchCalendar(deliveryLocationId?.value).then((data) => {
			if (data.error)
				setError(true);
			else
				setZapietData(data);

			isLoading && toggleLoading();
		});
	}, []);

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (selectedBillingDate.toLocaleDateString() === new Date().toLocaleDateString()) {
			// show confirmation to charge the user immediately
			return setChargePrompt(true);
		}

		reschedule();
	};

	const reschedule = async (placeOrder = false) => {
		toggleSubmitting();
		let response = { success: true };
		if (!isAlreadyScheduled) {
			// Convert the date to epoch time in seconds
			const billingDateEpoch = Math.floor(new Date(selectedBillingDate).getTime() / 1000);

			response = await dispatch(rescheduleOrder(billingDateEpoch, placeOrder));
			if (response.success) {
				toast.success(translation.subscription[placeOrder ? "order_placed" : "rescheduled"]);
				if (placeOrder)
					setChargePrompt(false);
			}
			else {
				toast.error(response.message);
			}
		}

		toggleSubmitting();
		if (toggleSurvey && response.success) {
			if (isFutureDate && !data.skipSurvey)
				toggleSurvey();
			else
				dispatch({ type: TOGGLE_RESCHEDULE_MODAL });
		}

		if (callback)
			callback(response, selectedBillingDate.toString());
	};

	if (error) {
		return <div className="mx-auto h-[250px] flex flex-col justify-center" >
			<h4 className="text-red-500 text-center">Error fetching available dates. Please try again later.</h4>
		</div>;
	}

	// Show the confirmation to charge the user immediately
	if (chargePrompt) {
		return (
			<PlaceOrder
				onCancel={() => setChargePrompt(false)}
				isSubmitting={isSubmitting}
				handlePlaceOrder={() => reschedule(true)}
			/>
		);
	} else if (showSurvey)
		// Show the survey if the user has rescheduled the order
		return <ReschedulingSurvey />;

	return (
		<form className="flex justify-center w-fit mx-auto flex-col gap-5 reschedule-container" onSubmit={handleSubmit}>
			{isLoading ? <div role="status" className="w-full animate-pulse">
				<div className="mx-auto bg-gray-100 w-60 h-[250px] dark:bg-gray-200" />
			</div> :
				<DatePicker
					selected={selectedDate} maxDate={maxDate} inline
					onChange={(date) => setSelectedDate(date)} minDate={new Date(zapietData?.minDateTime)}
					filterDate={(date) => filterAvailableDates(date, zapietData.disabled || [], latestOrderDeliveryDate)}
				/>}
			{isSubmitting ? <div className="min-h-12"> <Loader className="!size-7" /></div> :
				<button disabled={!isEnabled && isAlreadyScheduled}
					type="submit" className={clsx("btn-primary w-fit mx-auto", buttonClassName)}>
					{translation.user_address?.save}
				</button>}
			<span className="text-gray-500 text-center">
				{translation.subscription.current_date}: {formatDate(nextDeliveryDate, language)}
			</span>
		</form>
	);
};

export default RescheduleSubscription;
